import { EditableRootProps, useSlotRecipe, Editable as ChakraEditable, HStack, Icon } from "@chakra-ui/react"
import { CheckButton, CloseButton } from "../content/components/Button"
import { EditButton } from "../content/components/Button"
import editable from "./styling/editable"
import { NativeSelectField, NativeSelectRoot, NativeSelectItem } from "@hydrologiq/shared/chakra/NativeSelect"
import { useState, useRef, useEffect } from "react"
import { Tooltip } from "./Tooltip"
import { FaCircleInfo } from "react-icons/fa6"

interface EditableProps extends EditableRootProps {
  variant?: "hydrologiq"
  defaultValue: string
  disabled: boolean
  disabledHelperText?: string
  onValueSave: (value: string) => void
  items?: NativeSelectItem[]
  type?: "text" | "number"
  step?: string
}

export const Editable = (props: EditableProps) => {
  const { items, type = "text", step, disabled, disabledHelperText } = props
  const variant = props.variant ?? "hydrologiq"
  const recipe = useSlotRecipe({ recipe: editable })
  const styles = recipe({ variant })

  // Manage edit state completely ourselves, ignore Chakra's state
  const [isEditing, setIsEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState(props.defaultValue)

  const isSelect = items !== undefined
  const isSelectOpen = isSelect && isEditing
  const selectDefaultValue = items?.find((item) => item.label === props.defaultValue)?.value
  const [selectValue, setSelectValue] = useState(selectDefaultValue)

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && !isSelect && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing, isSelect])

  return (
    <ChakraEditable.Root
      css={styles.root}
      fontSize={"md"}
      onEditChange={() => {
        // Ignore Chakra's edit change events - we manage state ourselves
      }}
      onValueCommit={({ value }) => {
        if (isSelect) {
          selectValue && props.onValueSave(selectValue)
        } else {
          props.onValueSave(value)
        }
      }}
      disabled={props.disabled}
      activationMode={"none"}
      submitMode={"none"}
      edit={isEditing}
    >
      {!isEditing && (
        <ChakraEditable.Preview _hover={{ cursor: "default", backgroundColor: "unset" }} css={styles.preview}>
          {props.defaultValue}
        </ChakraEditable.Preview>
      )}

      {isEditing &&
        (isSelectOpen ? (
          <NativeSelectRoot>
            <NativeSelectField
              value={selectValue}
              defaultValue={selectDefaultValue}
              items={items}
              onChange={(e) => setSelectValue(e.target.value)}
            />
          </NativeSelectRoot>
        ) : (
          <ChakraEditable.Input
            ref={inputRef}
            css={styles.input}
            padding={0}
            type={type}
            step={step}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        ))}
      <ChakraEditable.Control css={styles.control}>
        {!isEditing ? (
          <HStack>
            <EditButton
              size={"2xs"}
              onClick={() => setIsEditing(true)}
              css={styles.editTrigger}
              aria-label="edit"
              disabled={disabled}
            />
            {disabled && disabledHelperText && (
              <Tooltip content={disabledHelperText}>
                <Icon as={FaCircleInfo} boxSize={4} color="primary" />
              </Tooltip>
            )}
          </HStack>
        ) : (
          <>
            <CloseButton
              size={"2xs"}
              onClick={() => setIsEditing(false)}
              css={styles.cancelTrigger}
              aria-label="cancel"
              disabled={disabled}
            />
            <CheckButton
              size={"2xs"}
              onClick={() => {
                // Handle save logic here
                if (isSelect) {
                  selectValue && props.onValueSave(selectValue)
                } else {
                  props.onValueSave(inputValue)
                }
                setIsEditing(false)
              }}
              css={styles.submitTrigger}
              aria-label="submit"
              disabled={disabled}
            />
          </>
        )}
      </ChakraEditable.Control>
    </ChakraEditable.Root>
  )
}
